/**
 * Login Page
 *
 * Supabase authentication for maritime personnel
 * With rate limiting protection (5 attempts per 15 minutes)
 */

import { useAuth } from '@/contexts/AuthContext';
import { LogIn, Ship, AlertTriangle, ShieldAlert, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loginRateLimiter, getRemainingAttemptsMessage } from '@/lib/utils/rateLimiter';
import { encryptedStorage } from '@/lib/auth/encryptedStorage';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    remaining: number;
    showWarning: boolean;
    isBlocked: boolean;
  }>({ remaining: 5, showWarning: false, isBlocked: false });

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/';

  // Check if session expired
  const searchParams = new URLSearchParams(location.search);
  const sessionExpired = searchParams.get('session_expired') === 'true';

  // Check rate limit status when email changes
  useEffect(() => {
    if (email) {
      const result = loginRateLimiter.check(email);
      setRateLimitInfo({
        remaining: result.remaining,
        showWarning: result.remaining <= 2 && result.remaining > 0,
        isBlocked: !result.allowed,
      });
    } else {
      setRateLimitInfo({ remaining: 5, showWarning: false, isBlocked: false });
    }
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Set "Remember Me" preference before login
      encryptedStorage.setRememberMe(rememberMe);
      
      await login(email, password);
      navigate(from, { replace: true });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
      
      // Update rate limit info after failed attempt
      const result = loginRateLimiter.check(email);
      setRateLimitInfo({
        remaining: result.remaining,
        showWarning: result.remaining <= 2 && result.remaining > 0,
        isBlocked: !result.allowed,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-4">
            <Ship size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">MARS-X</h1>
          <p className="text-gray-600 mt-2">Maintenance And Reporting System</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Sign In</h2>

          {/* Session Expired Alert (Blue/Info) */}
          {sessionExpired && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start gap-3">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-800">Session Expired</p>
                <p className="text-xs text-blue-700 mt-1">
                  Your session has expired for security. Please log in again.
                </p>
              </div>
            </div>
          )}

          {/* Rate Limit Warning (Yellow) */}
          {rateLimitInfo.showWarning && !rateLimitInfo.isBlocked && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-3">
              <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-yellow-800">
                  {getRemainingAttemptsMessage(rateLimitInfo.remaining)}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Your account will be temporarily locked after too many failed attempts.
                </p>
              </div>
            </div>
          )}

          {/* Rate Limit Blocked (Red) */}
          {rateLimitInfo.isBlocked && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
              <ShieldAlert size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Account Temporarily Locked</p>
                <p className="text-xs text-red-700 mt-1">
                  Too many failed login attempts. Please try again in 15 minutes.
                </p>
              </div>
            </div>
          )}

          {/* General Error */}
          {error && !rateLimitInfo.isBlocked && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="captain@example.com"
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember"
                  name="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={loading}
                />
                <label htmlFor="remember" className="ml-2 text-sm text-gray-700">
                  Remember me for 7 days
                </label>
              </div>
              {!rememberMe && (
                <span className="text-xs text-gray-500">30 min session</span>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || rateLimitInfo.isBlocked}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span>Signing in...</span>
              ) : rateLimitInfo.isBlocked ? (
                <span>Account Locked</span>
              ) : (
                <>
                  <LogIn size={20} />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Contact your system administrator for login credentials</p>
          </div>
        </div>

        {/* Login Information */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-semibold text-blue-800 mb-2">Need Access?</p>
          <p className="text-xs text-blue-700">
            Contact your system administrator for login credentials.
            <br />
            For support, refer to your organization's IT helpdesk.
          </p>
        </div>
      </div>
    </div>
  );
}
